import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import type { OfferListFilter, OfferResponse } from '../../types/offers';
import { useOffersT } from './offersI18n';
import {
  formatAed, formatDate, formatInstantDate, mapOfferError, offerActionErrorMessage, rowActionsFor, statusChipClass, type RowAction,
} from './offerUtils';
import ConfirmDialog from './ConfirmDialog';

const FILTERS: OfferListFilter[] = ['DRAFT', 'PENDING_APPROVAL', 'SCHEDULED', 'LIVE', 'PAUSED', 'ENDED', 'REJECTED', 'SUSPENDED', 'ARCHIVED'];
const PAGE_SIZE = 20;

/** Spec 062 — Promotions → Offers (moved out of PromotionsPage by Spec 081b, unchanged). */
export default function OffersTab() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { t, lang } = useOffersT();

  const [offers, setOffers] = useState<OfferResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<OfferListFilter | ''>('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [confirm, setConfirm] = useState<{ offer: OfferResponse; action: RowAction } | null>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.listMyOffers(token, filter || undefined, page, PAGE_SIZE);
      setOffers(res.content ?? []);
      setTotalPages(Math.max(res.totalPages ?? 1, 1));
    } catch (err) {
      setError(mapOfferError(err, t).code === 'FEATURE_DISABLED' ? t('offers.err.featureDisabled') : t('offers.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [token, filter, page, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(id);
  }, [toast]);

  const runAction = async () => {
    if (!confirm || !token) return;
    const { offer, action } = confirm;
    setActing(true);
    setActionError(null);
    try {
      const fn = {
        pause: api.pauseOffer,
        resume: api.resumeOffer,
        end: api.endOffer,
        archive: api.archiveOffer,
        withdraw: api.withdrawOffer,
      }[action];
      await fn(offer.id, token, offer.version);
      setConfirm(null);
      setToast(t('offers.toast.updated'));
      await load();
    } catch (err) {
      setActionError(offerActionErrorMessage(err, t));
    } finally {
      setActing(false);
    }
  };

  const header = (
    <div className="partner-services-header">
      <div className="partner-services-header-left">
        <h2>{t('offers.title')}</h2>
        <p>{t('offers.subtitle')}</p>
      </div>
      <button className="partner-services-add-btn offer-new-btn" onClick={() => navigate('/promotions/new')}>
        + {t('offers.new')}
      </button>
    </div>
  );

  const filterBar = (
    <div className="offer-toolbar">
      <label htmlFor="offer-status-filter" className="offer-toolbar__label">{t('offers.filter.label')}</label>
      <select
        id="offer-status-filter"
        className="offer-select"
        value={filter}
        onChange={(e) => { setFilter(e.target.value as OfferListFilter | ''); setPage(0); }}
      >
        <option value="">{t('offers.filter.all')}</option>
        {FILTERS.map((f) => (
          <option key={f} value={f}>{f === 'ARCHIVED' ? t('offers.filter.ARCHIVED') : t(`offers.status.${f}`)}</option>
        ))}
      </select>
    </div>
  );

  if (loading && offers.length === 0) {
    return (
      <div className="promo-tab-panel">
        {header}
        <p className="offer-muted-center" role="status">{t('offers.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="promo-tab-panel">
        {header}
        <div className="error-message" role="alert">{error}</div>
        <button type="button" className="offer-btn offer-btn--ghost" onClick={load}>{t('offers.retry')}</button>
      </div>
    );
  }

  // First-run empty state: no filter and nothing at all.
  if (!filter && offers.length === 0 && page === 0) {
    return (
      <div className="promo-tab-panel">
        <div className="offer-empty">
          <div className="offer-empty__icon" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" />
              <circle cx="7.5" cy="7.5" r="1.5" />
            </svg>
          </div>
          <h3>{t('offers.empty.title')}</h3>
          <p>{t('offers.empty.body')}</p>
          <ul className="offer-empty__rules">
            <li>{t('offers.empty.rule1')}</li>
            <li>{t('offers.empty.rule2')}</li>
            <li>{t('offers.empty.rule3')}</li>
          </ul>
          <button className="partner-services-add-btn offer-new-btn" onClick={() => navigate('/promotions/new')}>
            + {t('offers.new')}
          </button>
        </div>
      </div>
    );
  }

  const confirmCopy = confirm ? {
    title: t(`offers.confirm.${confirm.action}.title`),
    body: t(`offers.confirm.${confirm.action}.body`),
    label: t(`offers.action.${confirm.action}`),
    danger: confirm.action === 'end' || confirm.action === 'archive',
  } : null;

  return (
    <div className="promo-tab-panel">
      {header}
      {filterBar}

      {offers.length === 0 ? (
        <p className="offer-muted-center">{t('offers.filter.empty')}</p>
      ) : (
        <div className="partner-services-table-wrapper offer-table-wrapper">
          <table className="partner-services-table offer-table">
            <thead>
              <tr>
                <th>{t('offers.col.offer')}</th>
                <th>{t('offers.col.status')}</th>
                <th>{t('offers.col.window')}</th>
                <th>{t('offers.col.services')}</th>
                <th>{t('offers.col.budget')}</th>
                <th>{t('offers.col.results')}</th>
                <th>{t('offers.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((o) => {
                const { edit, actions } = rowActionsFor(o, filter === 'ARCHIVED');
                const title = (lang === 'ar' && o.titleAr) ? o.titleAr : o.titleEn;
                const count = o.items?.length ?? 0;
                return (
                  <tr key={o.id} data-testid={`offer-row-${o.id}`}>
                    <td>
                      <button type="button" className="partner-services-name-link offer-link" onClick={() => navigate(`/promotions/${o.id}`)}>
                        {title || '—'}
                      </button>
                      {o.pendingRevision && (
                        <div className="offer-subchip">{t('offers.pendingRevision')}</div>
                      )}
                      {o.adminEditedAt && (
                        <div className="offer-admin-edited" data-testid={`offer-admin-edited-${o.id}`}>
                          {t('offers.adminEdited', { date: formatInstantDate(o.adminEditedAt, lang) })}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={statusChipClass(o.status)}>{t(`offers.status.${o.status}`)}</span>
                      {o.hiddenReason && <div className="offer-hidden-reason">{t(`offers.hidden.${o.hiddenReason}`)}</div>}
                    </td>
                    <td className="offer-nowrap">
                      <span dir="ltr">{formatDate(o.startsAt, lang)} – {formatDate(o.endsAt, lang)}</span>
                    </td>
                    <td>{count === 1 ? t('offers.itemsCountOne') : t('offers.itemsCount', { n: count })}</td>
                    <td className="offer-nowrap">
                      <span dir="ltr">{o.bookingsUsed ?? 0} / {o.maxBookings ?? '∞'}</span>
                      {o.maxBookings == null && <span className="offer-muted"> ({t('offers.budget.unlimited')})</span>}
                    </td>
                    <td className="offer-results">
                      {t('offers.results', { views: o.views ?? 0, bookings: o.bookings ?? 0, discount: formatAed(o.discountGiven) })}
                    </td>
                    <td>
                      <div className="offer-row-actions">
                        <button type="button" className="offer-btn offer-btn--small" onClick={() => navigate(`/promotions/${o.id}`)}>
                          {edit ? t('offers.action.edit') : t('offers.action.view')}
                        </button>
                        {actions.map((a) => (
                          <button
                            key={a}
                            type="button"
                            className={`offer-btn offer-btn--small${a === 'end' || a === 'archive' ? ' offer-btn--danger-ghost' : ' offer-btn--ghost'}`}
                            onClick={() => { setActionError(null); setConfirm({ offer: o, action: a }); }}
                          >
                            {t(`offers.action.${a}`)}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="partner-pagination">
          <div className="partner-pagination-left" />
          <div className="partner-pagination-right">
            <span className="partner-pagination-info" dir="ltr">{page + 1} / {totalPages}</span>
            <button className="partner-pagination-btn" disabled={page <= 0} onClick={() => setPage(page - 1)} aria-label="Previous page">‹</button>
            <button className="partner-pagination-btn" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)} aria-label="Next page">›</button>
          </div>
        </div>
      )}

      {confirm && confirmCopy && (
        <ConfirmDialog
          title={confirmCopy.title}
          body={confirmCopy.body}
          confirmLabel={confirmCopy.label}
          cancelLabel={t('offers.confirm.cancel')}
          danger={confirmCopy.danger}
          busy={acting}
          error={actionError}
          onConfirm={runAction}
          onCancel={() => setConfirm(null)}
        />
      )}

      {toast && <div className="partner-toast success" role="status">{toast}</div>}
    </div>
  );
}

